// Лист «Настройки проекта» (IA заказчика, план PLAN_project_settings_and_header.md).
// Один дом для редкого проектного конфига: свёл в себя окно проектировщика,
// управление тарифом и вход в паспорт. Входы: ⚙ в шапке + пункт меню аккаунта.
// Принцип «дверь → комната»: в шапке статус + дверь, здесь — редактор (один на сущность).
import { useEffect, useRef, useState, Fragment } from 'react';
import { FileText, Users as UsersIcon, CreditCard, ChevronRight, FileDown, Landmark } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { DesignerPanel } from '@/components/SettingsTab';
import { PaymentModal } from '@/components/PaymentModal';
import { PassportPanel } from '@/components/PassportPanel';
import { tariffLabel, tariffBadgeColors } from '@/lib/tariff';
import type { Project } from '@/types/api';

export type SettingsSection = 'project' | 'designer' | 'tariff' | 'legal';

const SECTIONS: { id: SettingsSection; title: string; icon: typeof FileText; sub: string }[] = [
  { id: 'project',  title: 'Проект',        icon: FileText,   sub: 'Паспорт объекта, реквизиты' },
  { id: 'designer', title: 'Проектировщик', icon: UsersIcon,  sub: 'Пригласить, сменить, сбросить пароль' },
  { id: 'tariff',   title: 'Тариф и оплата', icon: CreditCard, sub: 'Текущий тариф, оплата по СБП или счёту' },
  { id: 'legal',    title: 'Документы для договора', icon: Landmark, sub: 'Пакет для проверки контрагента, реквизиты' },
];

// Реквизиты — дословно из описи пакета документов (BR-5: не по памяти).
const REQUISITES: [string, string][] = [
  ['Наименование', 'ООО «Рейл Проджект Профэшналз»'],
  ['ОГРН', '1206600036829'],
  ['ИНН', '6658535817'],
  ['КПП', '665801001'],
];

export function ProjectSettingsSheet({ project, initialSection = 'project', onClose }: {
  project: Project;
  initialSection?: SettingsSection;
  onClose: () => void;
}) {
  const projectId = project.id;
  const refs = useRef<Record<SettingsSection, HTMLElement | null>>({ project: null, designer: null, tariff: null, legal: null });
  const [payOpen, setPayOpen] = useState(false);
  const [passportOpen, setPassportOpen] = useState(false);

  // Прокрутка к запрошенной секции при открытии (дверь из шапки ведёт в свою комнату).
  useEffect(() => {
    const el = refs.current[initialSection];
    if (el) el.scrollIntoView({ block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tc = tariffBadgeColors(project.tariff ?? null);

  return (
    <>
      <Modal title="Настройки проекта" onClose={onClose} maxWidth={680}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {/* ── Проект ── */}
          <section ref={(el) => { refs.current.project = el; }}>
            <SectionHead icon={FileText} title="Проект" sub={SECTIONS[0].sub} />
            <div className="rounded-card border border-border" style={{ padding: 16, marginTop: 10 }}>
              <div className="text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em' }}>
                {shortCode(project.name)} · ID {projectId.slice(0, 8)}
              </div>
              <div className="text-ink" style={{ fontSize: 15, fontWeight: 600, marginTop: 4 }}>{stripCode(project.name)}</div>
              <button
                type="button"
                onClick={() => setPassportOpen(true)}
                className="inline-flex items-center rounded-btn"
                style={{ marginTop: 12, padding: '8px 14px', gap: 7, fontSize: 13, fontWeight: 500,
                         background: '#F3F4F6', color: '#0A1628', border: '1px solid #E5E7EB', cursor: 'pointer' }}
              >
                <FileText size={14} stroke="#6B7280" /> Открыть паспорт объекта <ChevronRight size={14} stroke="#9CA3AF" />
              </button>
            </div>
          </section>

          {/* ── Проектировщик ── */}
          <section ref={(el) => { refs.current.designer = el; }}>
            <SectionHead icon={UsersIcon} title="Проектировщик" sub={SECTIONS[1].sub} />
            <div style={{ marginTop: 10 }}>
              <DesignerPanel projectId={projectId} />
            </div>
          </section>

          {/* ── Тариф и оплата ── */}
          <section ref={(el) => { refs.current.tariff = el; }}>
            <SectionHead icon={CreditCard} title="Тариф и оплата" sub={SECTIONS[2].sub} />
            <div className="rounded-card border border-border" style={{ padding: 16, marginTop: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span className="inline-flex items-center rounded-pill" style={{ padding: '4px 12px', fontSize: 12, fontWeight: 600, background: tc.bg, color: tc.fg, border: `1px solid ${tc.border}` }}>
                {tariffLabel(project.tariff ?? null)}
              </span>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                onClick={() => setPayOpen(true)}
                className="inline-flex items-center rounded-btn"
                style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, gap: 6,
                         background: '#0A1628', color: '#fff', border: 'none', cursor: 'pointer' }}
              >
                <CreditCard size={14} /> {project.tariff ? 'Сменить тариф' : 'Оплатить'}
              </button>
            </div>
            <p className="text-muted" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
              Оплата по QR через СБП (тариф активируется автоматически) либо по счёту для юрлиц —
              способ выбирается в окне оплаты.
            </p>
          </section>

          {/* ── Документы для договора (юрлицам) ── */}
          <section ref={(el) => { refs.current.legal = el; }}>
            <SectionHead icon={Landmark} title="Документы для договора" sub={SECTIONS[3].sub} />
            <div className="rounded-card border border-border" style={{ padding: 16, marginTop: 10 }}>
              <p className="text-muted" style={{ fontSize: 12.5, lineHeight: 1.55, margin: 0 }}>
                Пакет учредительных документов исполнителя — для проверки службой безопасности
                и подготовки договора: устав, листы записи ЕГРЮЛ, свидетельство ИНН, решения
                о полномочиях, карта партнёра.
              </p>
              <a href="/legal/contract-package"
                className="inline-flex items-center rounded-btn"
                style={{ marginTop: 12, padding: '9px 16px', gap: 7, fontSize: 13, fontWeight: 600,
                         background: '#0A1628', color: '#fff', border: 'none',
                         textDecoration: 'none', cursor: 'pointer' }}>
                <FileDown size={14} /> Скачать пакет (ZIP, ~10 МБ)
              </a>
              <div style={{ borderTop: '1px solid #F3F4F6', marginTop: 14, paddingTop: 12,
                            display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 14px' }}>
                {REQUISITES.map(([k, v]) => (
                  <Fragment key={k}>
                    <span className="text-muted" style={{ fontSize: 12 }}>{k}</span>
                    <span className="text-ink tabular" style={{ fontSize: 12.5, fontWeight: 500 }}>{v}</span>
                  </Fragment>
                ))}
              </div>
              <p className="text-muted" style={{ fontSize: 11.5, lineHeight: 1.45, marginTop: 12, marginBottom: 0 }}>
                Карточка с образцами подписей (КОП) в пакет не входит — банковский документ,
                предоставляется по отдельному запросу.
              </p>
            </div>
          </section>

        </div>
      </Modal>

      {payOpen && <PaymentModal projectId={projectId} onClose={() => setPayOpen(false)} />}
      <PassportPanel open={passportOpen} projectId={projectId} onClose={() => setPassportOpen(false)} />
    </>
  );
}

function SectionHead({ icon: Icon, title, sub }: { icon: typeof FileText; title: string; sub: string }) {
  return (
    <div className="flex items-center" style={{ gap: 10 }}>
      <Icon size={18} stroke="#6B7280" />
      <div>
        <div className="text-ink" style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
        <div className="text-muted" style={{ fontSize: 12 }}>{sub}</div>
      </div>
    </div>
  );
}

function shortCode(name: string): string {
  const m = name.match(/^[А-ЯA-Z]{2,5}[-\s]?\d{1,6}/);
  return m ? m[0] : (name.split(/[—\-]/)[0].trim().slice(0, 12) || 'Проект');
}
function stripCode(name: string): string {
  return name.replace(/^[А-ЯA-Z]{2,5}[-\s]?\d{1,6}\s*[—\-:]\s*/, '').trim() || name;
}
